"use client";

import { GlobeAsiaAustraliaIcon } from "@heroicons/react/24/solid";
import { zodResolver } from "@hookform/resolvers/zod";
import valid from "card-validator";
import clsx from "clsx";
import creditCardType from "credit-card-type";
import { AnimatePresence, MotionConfig, motion } from "motion/react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import useMeasure from "react-use-measure";
import { toast } from "sonner";
import { z } from "zod";
import { PayButton, type PayButtonStatus } from "@/components/pay-button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useStateRef } from "@/lib/hooks/use-state-ref";
import {
  type CardPaymentResponse,
  PaycodeError,
  useCardPayment,
} from "@/lib/queries/payment";
import type { Banregio3DSResponse } from "@/lib/schemas/paycode/banregio-3ds-response";
import type { Card } from "@/lib/schemas/paycode/card";
import type { CardResponse } from "@/lib/schemas/paycode/card-response";
import type { MsiOptions } from "@/lib/schemas/paycode/msi-options";
import type { SafekeyResponse } from "@/lib/schemas/paycode/safekey-response";
import { useCheckoutStore } from "@/lib/store/checkout";
import { cn } from "@/lib/utils";
import { HeroIcon } from "../hero-icon";
import { Label } from "../ui/label";
import { EditProductsButton } from "./edit-products";
import { MsiPicker } from "./msi-picker";

type PaymentTranslator = (key: string) => string;

export const makeCardSchema = (t: PaymentTranslator) =>
  z.object({
    cardHolder: z
      .string()
      .min(1, t("errors.cardHolderRequired"))
      .regex(/^[\p{L}\s.'-]+$/u, t("errors.cardHolderInvalid")),
    cardNumber: z
      .string()
      .min(1, t("errors.cardNumberRequired"))
      .refine((v) => valid.number(v).isValid, t("errors.cardNumberInvalid")),
    expiry: z
      .string()
      .min(1, t("errors.expiryRequired"))
      .refine(
        (v) => valid.expirationDate(v).isValid,
        t("errors.expiryInvalid"),
      ),
    cvc: z
      .string()
      .min(1, t("errors.cvvRequired"))
      .refine((v) => valid.cvv(v, [3, 4]).isValid, t("errors.cvvInvalid")),
    msi: z.number().optional().nullable(),
    saveCard: z.boolean(),
  });

export type CardData = z.infer<ReturnType<typeof makeCardSchema>>;

export const toCard = (data: CardData): Card => ({
  card: data.cardNumber.replace(/\D/g, ""),
  cvc: data.cvc,
  expiry: data.expiry,
  name: data.cardHolder.trim(),
  msi: data.msi ?? 0,
  token: null,
  saveCard: data.saveCard,
});

const toTokenCard = (token: number, cvc: string): Card => ({
  card: "",
  cvc,
  expiry: "",
  name: "",
  msi: 0,
  token,
  saveCard: false,
});

interface Props {
  amount: number;
  isInternational?: boolean;
  msiOptions?: MsiOptions | null;
  onSuccess?: (trackCode: string) => void;
  onReject?: (message?: string) => void;
}

const cardBrandToImage: Record<string, keyof typeof cardImages> = {
  visa: "visa",
  mastercard: "mastercard",
  "american-express": "amex",
};

const waitForMaxLengthCardTypes = ["unionpay", "maestro"];

const getBrandImage = (brand: string) => {
  const key = brand.toLowerCase().replace(/\s+/g, "-");
  if (key in cardImages) return cardImages[key as keyof typeof cardImages];
  if (key === "american-express") return cardImages.amex;
  return cardImages.placeholder;
};

export const getCardImage = (value: string) => {
  const digits = value.replace(/\D/g, "");
  if (!digits) return cardImages.placeholder;
  const [card] = creditCardType(digits);
  const imageKey = card ? cardBrandToImage[card.type] : undefined;
  return imageKey ? cardImages[imageKey] : cardImages.placeholder;
};

export const formatCardNumber = (value: string) => {
  const digits = value.replace(/\D/g, "");
  const [card] = creditCardType(digits);
  const gaps = [...(card?.gaps ?? [4, 8, 12]), 16];
  const maxLength = card ? Math.max(...card.lengths) : 16;
  const trimmed = digits.slice(0, maxLength);

  let formatted = "";
  for (let i = 0; i < trimmed.length; i++) {
    if (gaps.includes(i)) formatted += " ";
    formatted += trimmed[i];
  }
  return formatted;
};

const countDigits = (value: string) => value.replace(/\D/g, "").length;

export const cursorAfterDigits = (formatted: string, digitCount: number) => {
  if (digitCount <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i]) && ++seen === digitCount) return i + 1;
  }
  return formatted.length;
};

export const applyCardNumberEdit = (
  raw: string,
  cursor: number,
  previous: string,
  inputType?: string,
) => {
  let digits = raw.replace(/\D/g, "");
  let digitsBeforeCursor = countDigits(raw.slice(0, cursor));

  const deletedOnlySpace =
    inputType?.startsWith("delete") &&
    raw.length < previous.length &&
    digits.length === countDigits(previous);
  if (deletedOnlySpace) {
    const forward = inputType === "deleteContentForward";
    const index = forward ? digitsBeforeCursor : digitsBeforeCursor - 1;
    if (index >= 0 && index < digits.length) {
      digits = digits.slice(0, index) + digits.slice(index + 1);
      if (!forward) digitsBeforeCursor -= 1;
    }
  }

  const formatted = formatCardNumber(digits);
  return {
    formatted,
    cursor: cursorAfterDigits(formatted, digitsBeforeCursor),
  };
};

export const formatExpiry = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 4);
  if (digits.length < 3) return digits;
  return `${digits.slice(0, 2)} / ${digits.slice(2)}`;
};

export const formatCVC = (value: string) =>
  value.replace(/\D/g, "").slice(0, 4);

const PaymentForm = ({
  amount,
  isInternational,
  msiOptions,
  onSuccess,
  onReject,
}: Props) => {
  const t = useTranslations("payment");
  const tc = useTranslations("common");
  const schema = useMemo(() => makeCardSchema(t), [t]);

  const setCard = useCheckoutStore((s) => s.setCard);
  const setLastRejection = useCheckoutStore((s) => s.setLastRejection);
  const payment = useCardPayment();
  const cardTokens = useCheckoutStore((s) => s.cardTokens);
  const tokenizable = useCheckoutStore((s) => s.tokenizable);
  const termsUrl = useCheckoutStore((s) => s.termsUrl);
  const business = useCheckoutStore((s) => s.business);

  const {
    control,
    handleSubmit,
    watch,
    setFocus,
    setValue,
    getValues,
    trigger,
    clearErrors,
    formState: { errors, isSubmitted },
  } = useForm<CardData>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: {
      cardHolder: "",
      cardNumber: "",
      expiry: "",
      cvc: "",
      msi: 0,
      saveCard: !!tokenizable,
    },
  });

  const [selectedToken, setSelectedToken] = useState<number | null>(null);
  const [status, setStatus, statusRef] = useStateRef<PayButtonStatus>("idle");

  const [elementRef, bounds] = useMeasure();

  const cardNumberRef = useRef<HTMLInputElement | null>(null);
  const expiryRef = useRef<HTMLInputElement | null>(null);
  const cvcRef = useRef<HTMLInputElement | null>(null);
  const cvvWrapRef = useRef<HTMLDivElement | null>(null);
  const advancedCardRef = useRef<string | null>(null);

  const cardNumber = watch("cardNumber");
  const cardImage = getCardImage(cardNumber);

  const handleTokenChange = (value: string) => {
    setSelectedToken(value === "new" ? null : Number(value));
    setValue("cvc", "");
    clearErrors("cvc");
  };

  const maybeAdvanceCard = (value: string) => {
    const digits = value.replace(/\D/g, "");
    if (advancedCardRef.current !== null) {
      if (digits.startsWith(advancedCardRef.current)) return;
      advancedCardRef.current = null;
    }
    const { isValid, card } = valid.number(value);
    if (!isValid || !card) return;
    if (
      waitForMaxLengthCardTypes.includes(card.type) &&
      digits.length < Math.max(...card.lengths)
    )
      return;
    advancedCardRef.current = digits;
    setFocus("expiry");
  };

  const handleCardNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    previous: string,
    onChange: (value: string) => void,
  ) => {
    const input = event.target;
    const { formatted, cursor } = applyCardNumberEdit(
      input.value,
      input.selectionStart ?? input.value.length,
      previous,
      (event.nativeEvent as InputEvent).inputType,
    );
    onChange(formatted);
    maybeAdvanceCard(formatted);
    requestAnimationFrame(() => {
      if (document.activeElement === input)
        input.setSelectionRange(cursor, cursor);
    });
  };

  const handleBackspace = (
    field: "expiry" | "cvc",
    value: string,
    key: string,
  ) => {
    if (key !== "Backspace" || value) return;
    setFocus(field === "expiry" ? "cardNumber" : "expiry");
  };

  const handleReject = (message?: string) => {
    setLastRejection(message);
    if (onReject) onReject(message);

    setStatus("error");
    setTimeout(() => setStatus("idle"), 2000);
  };

  const handleSuccess = (transaction: string = "") => {
    setLastRejection(undefined);
    setStatus("success");
    if (onSuccess) onSuccess(transaction);
  };

  const make3DSRequest = (url: string) => {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = url;

    document.body.appendChild(form);
    form.submit();
  };

  const handle3DSResponse = (data: Banregio3DSResponse) => {
    if (
      data.status === "Declinado" ||
      data.status === "Fallido" ||
      data.status === "Rechazado" ||
      data.status === "Timeout"
    ) {
      handleReject(data.status);
      return;
    }

    if (data.status === "Aprobado") {
      handleSuccess();
      return;
    }

    make3DSRequest(data.url3D);
  };

  // Has to be sent through a form because it needs to redirect automatically
  // and pass CORS restrictions.
  const makeSafekeyRequest = (url: string, creqData: string) => {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = url;

    const input = document.createElement("input");
    input.type = "hidden";
    input.name = "creq";
    input.value = creqData;

    form.appendChild(input);

    document.body.appendChild(form);
    form.submit();
  };

  const handleSafekey = (data: SafekeyResponse) => {
    if (
      data.authenticationStatus === "AUTHENTICATION_REJECTED" ||
      data.authorizationStatus === "AUTHORIZATION_DENIED" ||
      (data.approved === false &&
        data.authenticationStatus !== "CHALLENGE_REQUIRED")
    ) {
      handleReject(data.authenticationStatus ?? data.authorizationStatus);
      return;
    }

    if (
      data.authenticationStatus === "AUTHENTICATION_SUCCESSFUL" ||
      data.authorizationStatus === "AUTHORIZATION_SUCCESSFUL"
    ) {
      handleSuccess(data.transactionId);
      return;
    }

    const creq = {
      messageVersion: "2.2.0",
      messageType: "CReq",
      threeDSServerTransID: data.serverTransactionId,
      acsTransID: data.transactionId,
      challengeWindowSize: "04",
      sdkCounterStoA: "000",
    };
    const creqJsonStr = JSON.stringify(creq);
    const creqJsonB64 = Buffer.from(creqJsonStr).toString("base64");
    const creqData = creqJsonB64
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");

    makeSafekeyRequest(data.acsUrl, creqData);
  };

  const handleCardResponse = (data: CardResponse) => {
    if (data.status === "rechazada") {
      handleReject(data.status);
      return;
    }

    handleSuccess(data.transactionId || data.trackingCode);
  };

  const handlePaymentResponse = (data: CardPaymentResponse) => {
    const isSafekey = "acsUrl" in data;
    const is3DS = "url3D" in data;

    if (isSafekey) handleSafekey(data as SafekeyResponse);
    else if (is3DS) handle3DSResponse(data as Banregio3DSResponse);
    else handleCardResponse(data as CardResponse);
  };

  const submitPayment = () => {
    if (statusRef.current !== "idle") return;

    setLastRejection(undefined);
    setStatus("processing");
    payment.mutate(undefined, {
      onSuccess: handlePaymentResponse,
      onError: (error) => {
        const message =
          error instanceof PaycodeError
            ? error.message
            : tc("transactionRejectedToast");
        toast.error(message);
        handleReject(message);
      },
    });
  };

  const onSubmit = (data: CardData) => {
    if (statusRef.current !== "idle") return;
    setCard(toCard(data));
    submitPayment();
  };

  const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    if (statusRef.current !== "idle") return;

    if (amount <= 0) {
      event.preventDefault();
      toast.error(tc("amountMustBePositive"));
      return;
    }

    if (selectedToken !== null) {
      event.preventDefault();
      if (!(await trigger("cvc"))) return;
      setCard(toTokenCard(selectedToken, getValues("cvc")));
      submitPayment();
      return;
    }

    handleSubmit(onSubmit)(event);
  };

  const hasError =
    isSubmitted &&
    !!(errors.cardHolder || errors.cardNumber || errors.expiry || errors.cvc);
  const firstError = isSubmitted
    ? errors.cardHolder?.message ||
      errors.cardNumber?.message ||
      errors.expiry?.message ||
      errors.cvc?.message
    : undefined;

  return (
    <form onSubmit={handleFormSubmit} className="space-y-4">
      {msiOptions ? (
        <>
          <Controller
            control={control}
            name="msi"
            render={({ field }) => (
              <MsiPicker
                options={msiOptions}
                value={field.value ?? 0}
                onChange={field.onChange}
              />
            )}
          />
          <div className="h-px w-full bg-border/10" />
        </>
      ) : null}

      {cardTokens && cardTokens.length > 0 ? (
        <>
          <div className="flex items-end gap-2">
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <Label className="ml-2">{t("savedCards")}</Label>
              <Select
                value={selectedToken !== null ? String(selectedToken) : "new"}
                onValueChange={handleTokenChange}
              >
                <SelectTrigger className="min-h-12 w-full rounded-lg">
                  <SelectValue placeholder={t("selectCard")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">{t("newCard")}</SelectItem>
                  {cardTokens.map((token) => (
                    <SelectItem key={token.id} value={String(token.id)}>
                      {(() => {
                        const image = getBrandImage(token.brand);
                        const isPlaceholder = image === cardImages.placeholder;
                        return (
                          <span className="flex items-center gap-2">
                            <Image
                              width={32}
                              height={21}
                              alt={token.brand}
                              src={image}
                            />
                            {isPlaceholder ? `${token.brand} ` : ""}••••{" "}
                            {token.card}
                          </span>
                        );
                      })()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <AnimatePresence initial={false}>
              {selectedToken !== null ? (
                <motion.div
                  key="cvc"
                  ref={cvvWrapRef}
                  layout
                  transition={{ duration: 0.27, ease: [0.19, 1, 0.22, 1] }}
                  initial={{ width: 0, opacity: 0 }}
                  animate={{ width: "18%", opacity: 1 }}
                  exit={{ width: 0, opacity: 0 }}
                  onAnimationComplete={() =>
                    cvvWrapRef.current?.querySelector("input")?.focus()
                  }
                  className="shrink-0 overflow-hidden"
                >
                  <Controller
                    control={control}
                    name="cvc"
                    render={({ field }) => (
                      <Input
                        placeholder={t("cvv")}
                        maxLength={4}
                        autoComplete="off"
                        type="password"
                        inputMode="numeric"
                        className={cn(
                          "min-h-12 rounded-lg",
                          errors.cvc &&
                            "focus-visible:border-destructive focus-visible:ring-destructive/20",
                        )}
                        value={field.value}
                        onChange={(e) =>
                          field.onChange(formatCVC(e.target.value))
                        }
                        onBlur={field.onBlur}
                      />
                    )}
                  />
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
          <div className="h-px w-full bg-border/10" />
        </>
      ) : null}

      <MotionConfig transition={{ duration: 0.27, ease: [0.19, 1, 0.22, 1] }}>
        <motion.div animate={{ height: bounds.height }}>
          <div ref={elementRef}>
            <AnimatePresence initial={false} mode="popLayout">
              {selectedToken !== null ? (
                <motion.button
                  key="new-card-btn"
                  initial={{
                    transform: "translateY(16px)",
                    opacity: 0,
                    filter: "blur(2px)",
                  }}
                  animate={{
                    transform: "translateY(0px)",
                    opacity: 1,
                    filter: "blur(0px)",
                  }}
                  exit={{
                    transform: "translateY(16px)",
                    opacity: 0,
                    filter: "blur(2px)",
                  }}
                  type="button"
                  className="group flex min-h-12 w-full cursor-pointer flex-row items-center gap-x-2 rounded-lg border border-border bg-background px-2 text-start hover:bg-top"
                  onClick={() => handleTokenChange("new")}
                >
                  <HeroIcon
                    name="credit-card"
                    className="h-5 w-5 text-muted group-hover:text-foreground"
                  />
                  <p className="text-sm">{t("enterNewCard")}</p>
                </motion.button>
              ) : (
                <motion.div
                  key="card-form"
                  initial={{
                    transform: "translateY(-16px)",
                    opacity: 0,
                    filter: "blur(2px)",
                  }}
                  animate={{
                    transform: "translateY(0px)",
                    opacity: 1,
                    filter: "blur(0px)",
                  }}
                  exit={{
                    transform: "translateY(-16px)",
                    opacity: 0,
                    filter: "blur(2px)",
                  }}
                >
                  <Controller
                    control={control}
                    name="cardHolder"
                    render={({ field }) => (
                      <Input
                        placeholder={t("cardHolderPlaceholder")}
                        autoComplete="cc-name"
                        className={cn(
                          "mb-2",
                          hasError &&
                            !!errors.cardHolder &&
                            "focus-visible:border-destructive focus-visible:ring-destructive/20",
                        )}
                        value={field.value}
                        name="cardHolder"
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                      />
                    )}
                  />
                  <div
                    className={clsx(
                      "relative flex h-28 cursor-text flex-col justify-between divide-y rounded-lg border border-border bg-background px-4 transition-all duration-200 focus-within:border-accent focus-within:bg-top focus-within:ring-[3px] focus-within:ring-accent/20 md:h-14 md:flex-row md:items-center md:divide-y-0",
                      hasError &&
                        !errors.cardHolder &&
                        "focus-within:border-destructive focus-within:ring-destructive/20",
                    )}
                  >
                    <div className="flex h-14 basis-1/2 items-center border-border">
                      <div className="relative mr-2">
                        <Image
                          width={32}
                          height={32}
                          alt="Credit card type icon"
                          src={cardImage}
                        />
                        {isInternational && (
                          <div className="-top-2 -right-2 absolute flex h-4 w-4 items-center justify-center rounded-md bg-white">
                            <GlobeAsiaAustraliaIcon className="h-3 w-3 text-black" />
                          </div>
                        )}
                      </div>
                      <Controller
                        control={control}
                        name="cardNumber"
                        render={({ field }) => (
                          <input
                            ref={(e) => {
                              field.ref(e);
                              cardNumberRef.current = e;
                            }}
                            className="w-full min-w-0 border-none bg-transparent font-medium outline-none"
                            id="ccInput"
                            placeholder={t("cardNumberPlaceholder")}
                            autoComplete="cc-number"
                            value={field.value}
                            onChange={(e) =>
                              handleCardNumberChange(
                                e,
                                field.value,
                                field.onChange,
                              )
                            }
                          />
                        )}
                      />
                    </div>
                    <div className="flex h-14 basis-1/2 items-center">
                      <div className="basis-1/2 md:basis-2/3">
                        <Controller
                          control={control}
                          name="expiry"
                          render={({ field }) => (
                            <input
                              ref={(e) => {
                                field.ref(e);
                                expiryRef.current = e;
                              }}
                              className="w-full min-w-0 border-none bg-transparent text-base outline-none md:text-right"
                              id="ccExpiryInput"
                              placeholder="MM / YY"
                              autoComplete="cc-exp"
                              value={field.value}
                              onChange={(e) => {
                                const formatted = formatExpiry(e.target.value);
                                field.onChange(formatted);
                                if (formatted.replace(/\s\/\s/, "").length >= 4)
                                  setFocus("cvc");
                              }}
                              onKeyDown={(e) =>
                                handleBackspace("expiry", field.value, e.key)
                              }
                            />
                          )}
                        />
                      </div>
                      <div className="basis-1/3">
                        <Controller
                          control={control}
                          name="cvc"
                          render={({ field }) => (
                            <input
                              ref={(e) => {
                                field.ref(e);
                                cvcRef.current = e;
                              }}
                              className="w-full min-w-0 border-none bg-transparent text-base outline-none md:text-right"
                              id="ccCVCInput"
                              placeholder={t("cvv")}
                              maxLength={5}
                              autoComplete="off"
                              type="password"
                              value={field.value}
                              onChange={(e) =>
                                field.onChange(formatCVC(e.target.value))
                              }
                              onKeyDown={(e) =>
                                handleBackspace("cvc", field.value, e.key)
                              }
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                  {tokenizable && business !== "ABBE LEASING" ? (
                    <Controller
                      control={control}
                      name="saveCard"
                      render={({ field }) => (
                        <Label className="mt-4 flex cursor-pointer items-center gap-2 text-foreground text-sm">
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={(c) => field.onChange(c === true)}
                          />
                          {t("saveCard")}
                        </Label>
                      )}
                    />
                  ) : null}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </MotionConfig>
      <div className="mt-2 h-4 pl-1">
        {(selectedToken !== null ? errors.cvc?.message : firstError) ? (
          <p className="text-destructive text-sm">
            {selectedToken !== null ? errors.cvc?.message : firstError}
          </p>
        ) : null}
      </div>
      <div className="mt-4 flex flex-col items-center justify-center">
        <EditProductsButton className="group mb-3 w-1/2 text-muted text-xs">
          {t.rich("editableProductsNote", {
            click: (chunks) => (
              <span className="text-accent group-hover:underline">
                {chunks}
              </span>
            ),
          })}
        </EditProductsButton>
        <PayButton type="submit" amount={amount} status={status} />
        <a
          className="mt-3 font-medium text-accent text-xs hover:opacity-80"
          target="_blank"
          href={termsUrl || "https://paycode.com.mx/terms.html"}
          rel="noopener noreferrer"
        >
          {tc("termsAndConditions")}
        </a>
      </div>
    </form>
  );
};

export const PaymentFormSkeleton = ({
  msi = false,
  savedCards = false,
  tokenizable = false,
}: {
  msi?: boolean;
  savedCards?: boolean;
  tokenizable?: boolean;
}) => (
  <div className="space-y-4">
    {msi && (
      <>
        <Skeleton className="h-14 w-full rounded-lg" />
        <div className="h-px w-full" />
      </>
    )}
    {savedCards && (
      <>
        <div className="flex flex-col gap-1.5">
          <Skeleton className="ml-2 h-4 w-24" />
          <Skeleton className="h-12 w-full rounded-lg" />
        </div>
        <div className="h-px w-full" />
      </>
    )}
    <div>
      <Skeleton className="mb-2 h-12 w-full rounded-lg" />
      <Skeleton className="h-28 w-full rounded-lg md:h-14" />
      {tokenizable && (
        <div className="mt-4 flex flex-row items-center gap-2">
          <Skeleton className="h-5 w-5" />
          <Skeleton className="h-5 w-64" />
        </div>
      )}
    </div>
    <div className="mt-2 h-4 pl-1" />
    <div className="mt-4 flex flex-col items-center justify-center">
      <Skeleton className="h-12 w-40 rounded-full" />
      <Skeleton className="mt-3 h-4 w-36" />
    </div>
  </div>
);

export const cardImages = {
  placeholder:
    "data:image/svg+xml;base64,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",
  visa: "data:image/svg+xml;base64,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",
  mastercard:
    "data:image/svg+xml;base64,PHN2ZyBmb2N1c2FibGU9ImZhbHNlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAzMiAyMSI+ICA8ZyBpZD0iUGFnZS0xIiBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPiAgICA8ZyBpZD0ibWFzdGVyY2FyZCI+ICAgICAgPGcgaWQ9ImNhcmQiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDAgMikiPiAgICAgICAgPHBhdGggaWQ9InNoYXBlIiBmaWxsPSIjMDAzNjYzIiBkPSJNMjYuNTggMTlIMi40MkEyLjQgMi40IDAgMCAxIDAgMTYuNjJWMi4zOEEyLjQgMi40IDAgMCAxIDIuNDIgMGgyNC4xNkEyLjQgMi40IDAgMCAxIDI5IDIuMzh2MTQuMjVBMi40IDIuNCAwIDAgMSAyNi41OCAxOXoiLz4gICAgICAgIDxjaXJjbGUgaWQ9InNoYXBlIiBjeD0iMTAuNSIgY3k9IjkuNSIgcj0iNi41IiBmaWxsPSIjRUIxQzI2Ii8+ICAgICAgICA8Y2lyY2xlIGlkPSJzaGFwZSIgY3g9IjE4LjUiIGN5PSI5LjUiIHI9IjYuNSIgZmlsbD0iI0Y5OUYxQiIvPiAgICAgICAgPHBhdGggaWQ9InNoYXBlIiBmaWxsPSIjRUY1RDIwIiBkPSJNMTQuNSA0LjM4YTYuNDkgNi40OSAwIDAgMCAwIDEwLjI0IDYuNDkgNi40OSAwIDAgMCAwLTEwLjI0eiIvPiAgICAgIDwvZz4gICAgPC9nPiAgPC9nPjwvc3ZnPg==",
  amex: "data:image/svg+xml;base64,PHN2ZyBmb2N1c2FibGU9ImZhbHNlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAzMiAyMSI+ICA8ZyBpZD0iUGFnZS0xIiBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPiAgICA8ZyBpZD0iYW1leCI+ICAgICAgPGcgaWQ9ImNhcmQiIHRyYW5zZm9ybT0idHJhbnNsYXRlKC0xIDIpIj4gICAgICAgIDxwYXRoIGlkPSJzaGFwZSIgZmlsbD0iIzFEOTFDRSIgZD0iTTI3LjU4IDE5SDMuNDJBMi40IDIuNCAwIDAgMSAxIDE2LjYyVjIuMzhBMi40IDIuNCAwIDAgMSAzLjQyIDBoMjQuMTZBMi40IDIuNCAwIDAgMSAzMCAyLjM4djE0LjI1QTIuNCAyLjQgMCAwIDEgMjcuNTggMTl6Ii8+ICAgICAgICA8cG9seWxpbmUgaWQ9InNoYXBlIiBmaWxsPSIjRkZGRkZGIiBwb2ludHM9IjUuMDYzIDExLjg5NiA0LjU5MSAxMyAuMzYgMTMgMy42OTkgNiAxMS42NzQgNiAxMi40NzkgNy41NDYgMTMuMjMxIDYgMTYuMDY0IDYgMTcuNDkyIDYgMjMuOTgzIDYgMjQuOTAyIDYuOTYxIDI1Ljg4OSA2IDMwLjg4MiA2IDI3LjMzNyA5LjQ5MiAzMC43MjkgMTMgMjUuODk3IDEzIDI0LjgxNiAxMS45NjkgMjMuNzQ0IDEzIDE3LjQ5MiAxMyAxNi4wNjQgMTMgNi40OTYgMTMgNS45NzcgMTEuODk2Ii8+ICAgICAgICA8cGF0aCBpZD0ic2hhcGUiIGZpbGw9IiMxRDkxQ0UiIGQ9Ik01Ljk4IDExLjk3aC0uOTIuOTJ6TTE2LjIgN2gtMi4xbC0xLjU4IDMuMzVMMTAuODIgN2gtMi4xdjQuODVMNi41NSA3SDQuNThsLTIuMzIgNWgxLjQybC40Ny0xLjE0aDIuN0w3LjM5IDEySDEwVjcuOTNMMTEuODUgMTJoMS4yMmwxLjg0LTR2NGgxLjI5Vjd6bTguNjcgMS42MkwyMy4zNyA3aC02LjAydjVIMjMuMTdsMS42NS0xLjY0TDI2LjQ4IDEyaDEuNTVsLTIuMzctMi41M0wyOC4xIDdoLTEuNjJsLTEuNjEgMS42MnpNMjEuNyAxMWgtMy4wNlY5LjloMy4wNlY4LjloLTMuMDZWOGgzLjA2di0uODVsMi4yNyAyLjI3LTIuMjcgMi4yOFYxMXpNNS41MyA3LjgybC44OCAyLjAzSDQuNThsLjk1LTIuMDN6Ii8+ICAgICAgPC9nPiAgICA8L2c+ICA8L2c+PC9zdmc+",
};

export default PaymentForm;
